import { z } from 'zod';
import { AUTHORED_LOCALES, type AuthoredLocale } from './enums';

/** Text authored in every authored locale (zh-HK, en). Content validation rejects a missing one. */
export const LocalizedText = z.object(
  Object.fromEntries(AUTHORED_LOCALES.map((l) => [l, z.string().min(1)])) as Record<AuthoredLocale, z.ZodString>,
);
export type LocalizedText = z.infer<typeof LocalizedText>;

export const Id = z.string().min(1).max(64);

export const ApiErrorCode = z.enum([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'stale_revision',
  'idempotency_mismatch',
  'rate_limited',
  'insufficient_credits',
  /** Not enough ink coins in the save (energy refills, lesson skips, extra shifts, outfits). */
  'insufficient_coins',
  'insufficient_energy',
  'daily_limit',
  'content_blocked',
  /** Illustrated manga can't be drawn right now (paused by ops or today's art budget is spent); nothing was charged. */
  'illustration_unavailable',
  /** A guest with a game signed in with an email or Apple ID whose account has a game of its own: refused, so neither game is lost (they use another sign-in). */
  'account_exists',
  'unavailable',
  'internal',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCode>;

export const ApiError = z.object({
  error: z.object({
    code: ApiErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

export const CursorQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type CursorQuery = z.infer<typeof CursorQuery>;

/** Header names are part of the contract; both sides import them rather than retyping strings. */
export const HEADERS = {
  idempotencyKey: 'idempotency-key',
  requestId: 'x-request-id',
  locale: 'accept-language',
  clientVersion: 'x-client-version',
} as const;

